/*
 * Copyright (c) 2020 CalAmp Corp.  All Rights Reserved
 */
import React from 'react';
import {Theme, Typography} from '@material-ui/core';
import Paper from '@material-ui/core/Paper';
import {makeStyles} from '@material-ui/core/styles';
import {translate} from '@ion/core/src/locale/locale';
import {TertiaryIconButton} from '../components/buttons/TertiaryIconButton';
import {NotificationGeneralAlertIcon} from '../icons/NotificationGeneralAlertIcon';
import {NotificationRefreshIcon} from '../icons/NotificationRefreshIcon';
import {dlsColor} from '../theme/theme';

// defines the prop definitions for the error states component ...
export type ErrorStateProps = {
  show: boolean;
  isRTE?: boolean;
  isCondensedVariant?: boolean;
  onRefresh?: (e: any) => void;
  errorText?: string;
};

// defines the styles used by this component ...
const useStyles = makeStyles<Theme, ErrorStateProps>(theme => ({
  paper : {
    width      : '100%',
    height     : '100%',
    boxShadow  : 'none !important',
    background : 'none !important'
  },
  container : {
    width          : '100%',
    height         : '100%',
    display        : 'flex',
    alignItems     : 'center',
    justifyContent : 'center',
    flexDirection  : 'column'
  },
  typography : {
    marginTop : 20
  },
  altTypography : {
    color          : dlsColor('grey1'),
    fontSize       : '14px',
    lineHeight     : '24px',
    width          : '100%',
    display        : 'flex',
    alignItems     : 'center',
    justifyContent : 'center',
    marginTop      : 10
  },
  btn : {
    marginTop : '2%'
  },
  altBtn : {
    marginTop : 0,
    fontSize  : '14px !important'
  }
}));

/**
 * The ErrorState component serves to provide the generic loading failure notification
 * within a component. Components which require displaying external service results are
 * to include the ErrorState component and manage thier display via the show prop, while
 * also providing an onRefresh prop which allows the user to reload the component's data.
 *
 * @see https://www.figma.com/file/z9yUzIaskZRhgpYIhLyh91/03_Hero-Screens?node-id=632%3A82003
 *
 * This component requires applications to define an "notifications.json" translation
 * document with the following keys:
 *
 *   - notifications:error.message
 *   - notifications:error.refresh
 *
 * ```typescript
 *   import {ErrorState} from '@ion/dls/src/notifications';
 *
 *   export const SomeComponent = ({...}) => {
 *     const dispatch     = useDispatch();
 *     const loadingError = useSelector(({state}) => state.loadingError);
 *     if (loadingError) {
 *       <ErrorState show onRefresh={function() {
 *         dispatch(loadItems());
 *       }} />
 *     }
 *     ...
 *   };
 *
 * ```
 *
 */
export const ErrorState = (props: ErrorStateProps) => {
  const {show, isRTE, onRefresh, errorText, isCondensedVariant = false} = props;
  const classes = useStyles(props);
  if (show) {
    return (
      <Paper className={classes.paper} elevation={0}>
        <div className={classes.container}>
          <NotificationGeneralAlertIcon />
          <Typography variant='body1' className={isCondensedVariant ? classes.altTypography : classes.typography}>
            {errorText || translate(`notifications:error.${!isRTE ? 'server' : 'client'}`)}
          </Typography>
          <TertiaryIconButton className={isCondensedVariant ? classes.altBtn : classes.btn}
            onClick={onRefresh}
            label={translate('notifications:error.refresh')}
            icon={<NotificationRefreshIcon />}
            iconDirection='left'
            large />
        </div>
      </Paper>
    );
  }
  return null;
};
export default ErrorState;
